import { QTabContext, QTabList, QTab, QTabPanel } from '@kw/quokka-ui';
import { useState, ChangeEvent, useMemo, useEffect } from 'react';
import { useBeforeUnload } from 'react-router-dom';
import styled from '@mui/system/styled';
import { OverviewTab } from './OverviewTab';
import { AggregateDataProps } from '../types/ReportTypes';
import { BehaviorsTab } from './BehaviorsTabs';
import { NetworkInfoTab } from './NetworkInfoTab';
import { MaliciousTab } from './MaliciousTab';
import { useReloadRestore } from '../utils/useReloadRestore';
import { ApplicationDetails } from '../types/ApplicationDetailsType';
import { useIsMount } from '../utils/useIsMount';
import SbomTab from './SbomTab';
import { RegulationsTab } from './RegulationsTab';

export function RootTabs({
  aggregateData,
  ruleSummaries,
  analysisId,
  maliciousResult,
  os,
  appDetails,
  sbomData
}: AggregateDataProps & {
  ruleSummaries: any[];
  analysisId: string;
  maliciousResult: any;
  os: string;
  appDetails: ApplicationDetails['current'];
  sbomData?: any[];
}) {
  function a11yProps(index: number) {
    return {
      id: `settings-tab-${index}`,
      'aria-controls': `settings-tabpanel-${index}`,
    };
  }

  const { appDetailReportTabFocus, setAppDetailReportTabFocus, setRestoredFilters, setShouldRestore, setRestoredSearchQuery } =
    useReloadRestore();
  const [activeTab, setActiveTab] = useState<number>(appDetailReportTabFocus.root);

  // page reload saves active tab in local storage
  useBeforeUnload(() => {
    setAppDetailReportTabFocus({ ...appDetailReportTabFocus, root: activeTab });
    setShouldRestore(true);
  });

  const isMount = useIsMount();
  // navigating between tabs tracks root tab but resets filters and sub tabs
  useEffect(() => {
    setAppDetailReportTabFocus({ overview: 0, network: 0, root: activeTab });
    setShouldRestore(false);
    setRestoredFilters({});
    if (!isMount) {
      setRestoredSearchQuery('');
    }
  }, [activeTab]);

  const handleChange = (_event: ChangeEvent<HTMLButtonElement>, tab: number) => {
    setActiveTab(tab);
  };

  const behavioralRuleSummaries = useMemo(() => {
    return ruleSummaries?.filter(
      ruleSummary =>
        (ruleSummary.rule?.tool === 'pulsar' || ruleSummary.rule?.tool === 'pulsar-ios') &&
        ruleSummary.rule?.properties?.category === 'behavioral',
    );
  }, [ruleSummaries]);

  const maliciousRuleSummaries = useMemo(() => {
    return ruleSummaries.filter(
      ruleSummary =>
        (ruleSummary.rule?.tool === 'pulsar' || ruleSummary.rule?.tool === 'pulsar-ios' || ruleSummary.rule?.tool === 'permoid') &&
        ruleSummary.rule?.id?.startsWith('profile'),
    );
  }, [ruleSummaries]);

  return (
    <QTabContext value={activeTab}>
      <QTabList tabs="true" onChange={handleChange} variant="fullWidth">
        <QTab label="Overview" value={0} {...a11yProps(0)} />
        <QTab label="Behaviors" value={1} {...a11yProps(1)} />
        <QTab label="Network Info" value={2} {...a11yProps(2)} />
        {os === 'android' && <QTab label="SBOM" value={3} {...a11yProps(3)} />}
        {os === 'android' && <QTab label="Malicious Report" value={4} {...a11yProps(4)} />}
        <QTab label="Regulations" value={5} {...a11yProps(5)} />
      </QTabList>
      <StyledQTabPanel tabid="0" value={0} index={0}>
        <OverviewTab
          aggregateData={aggregateData}
          ruleSummaries={ruleSummaries}
          analysisId={analysisId}
          maliciousResult={maliciousResult}
          os={os}
        />
      </StyledQTabPanel>
      <StyledQTabPanel tabid="1" value={1} index={1}>
        <BehaviorsTab ruleSummaries={behavioralRuleSummaries} analysisId={analysisId} />
      </StyledQTabPanel>
      <StyledQTabPanel tabid="2" value={2} index={2}>
        <NetworkInfoTab appDetails={appDetails} />
      </StyledQTabPanel>
      <StyledQTabPanel tabid="3" value={3} index={3}>
        {os === 'android' && <SbomTab sbomData={sbomData} />}
      </StyledQTabPanel>
      <StyledQTabPanel tabid="4" value={4} index={4}>
        {os === 'android' && (
          <MaliciousTab
            ruleSummaries={maliciousRuleSummaries}
            analysisId={analysisId}
            maliciousResult={maliciousResult}
            aggregateData={aggregateData}
          />
        )}
      </StyledQTabPanel>
      <StyledQTabPanel tabid="5" value={5} index={5}>
        <RegulationsTab analysisId={analysisId} platform={os} />
      </StyledQTabPanel>
    </QTabContext>
  );
}

const StyledQTabPanel = styled(QTabPanel)(() => ({
  padding: '40px 0px',
}));
